import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Rotas do Navegador Premium (GPS do smartphone/navegador) — "Rota via App".
 * MVP em localStorage; migrar p/ backend `/v1/navigation` (FASE D).
 */

export interface RouteCoord {
  lat: number;
  lon: number;
  t: number; // timestamp
  speed?: number; // nós
}

export interface AppRoute {
  id: string;
  name: string;
  origin: 'app';
  startedAt: number;
  finishedAt: number;
  distanceM: number;
  maxSpeedKn: number;
  avgSpeedKn: number;
  coords: RouteCoord[];
  destName?: string;
}

interface NavRoutesState {
  routes: AppRoute[];
  add: (r: Omit<AppRoute, 'id' | 'origin'>) => void;
  remove: (id: string) => void;
}

function uid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `rt-${Math.random().toString(36).slice(2)}`;
}

export const useNavRoutesStore = create<NavRoutesState>()(
  persist(
    (set) => ({
      routes: [],
      add: (r) => set((s) => ({ routes: [{ ...r, id: uid(), origin: 'app' as const }, ...s.routes].slice(0, 200) })),
      remove: (id) => set((s) => ({ routes: s.routes.filter((x) => x.id !== id) })),
    }),
    { name: 'nautmax-app-routes' },
  ),
);

const R = 6371000; // m
export function haversineM(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const p1 = (a.lat * Math.PI) / 180;
  const p2 = (b.lat * Math.PI) / 180;
  const dp = ((b.lat - a.lat) * Math.PI) / 180;
  const dl = ((b.lon - a.lon) * Math.PI) / 180;
  const x = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return R * 2 * Math.asin(Math.min(1, Math.sqrt(x)));
}

export function bearingDeg(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const p1 = (a.lat * Math.PI) / 180;
  const p2 = (b.lat * Math.PI) / 180;
  const dl = ((b.lon - a.lon) * Math.PI) / 180;
  const y = Math.sin(dl) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  return (Math.atan2(y, x) * 180) / Math.PI;
}

const COMPASS = ['N', 'NE', 'L', 'SE', 'S', 'SO', 'O', 'NO'];
export function compassPt(deg: number): string {
  return COMPASS[Math.round((((deg % 360) + 360) % 360) / 45) % 8];
}
